import React from 'react';
import { SvgIcon, SvgIconProps, useTheme } from '@mui/material';
import {
  PounceStyleProps,
  extractPounceProps,
  mapPouncePropsToSx,
  resolveThemeColor,
} from '../../utils';
import type { IconType } from '../../theme/icons';

export type IconSize = 'x-small' | 'small' | 'medium' | 'large' | 'xlarge';

export interface IconProps
  extends Omit<SvgIconProps, 'fontSize' | 'color' | keyof PounceStyleProps>, PounceStyleProps {
  /** The accessible title */
  title?: string;

  /** The accessible description */
  description?: string;

  /** The icon that you want to show */
  type: IconType;

  /** The color of the icon - supports pounce color tokens (e.g., 'blue-400') */
  color?: string;

  /** The size of the icon */
  size?: IconSize | number;
}

/**
 * PounceV2 Icon component built on top of MUI SvgIcon.
 *
 * A simple SVG element exported as a React component. It renders an SVG with icon paths
 * from the pounce theme. Maintains the same API as the original pounce Icon component
 * while leveraging MUI's SvgIcon component for proper accessibility and styling.
 *
 * Features:
 * - Full pounce API compatibility (size, color, type)
 * - Automatic theme icon resolution from pounce theme
 * - MUI SvgIcon benefits (proper color inheritance, accessibility, theming)
 * - Flexible sizing (predefined sizes or custom pixel values)
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { title, description, type, size = 'large', color, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const muiTheme = useTheme();
  // Access pounce icons from MUI theme customization
  const pounceIcons = (muiTheme as any).pounce?.icons;

  const icon = pounceIcons?.[type] ?? pounceIcons?.unknown;
  const viewBox = icon?.viewBox || '0 0 24 24';

  let sizeInPx;
  if (typeof size === 'number') {
    sizeInPx = size;
  } else {
    switch (size) {
      case 'x-small':
        sizeInPx = 12;
        break;
      case 'small':
        sizeInPx = 16;
        break;
      case 'medium':
        sizeInPx = 20;
        break;
      case 'large':
      case 'xlarge':
      default:
        sizeInPx = 24;
    }
  }

  // Resolve color: supports pounce tokens (e.g., 'blue-400') and theme token shortcuts (e.g., 'primary', 'error')
  const { tokens } = muiTheme.palette;
  const themeTokenColors: Record<string, string> = {
    primary: tokens?.primaryMain,
    secondary: tokens?.textSecondary,
    success: tokens?.success,
    warning: tokens?.warning,
    error: tokens?.error,
  };

  let resolvedColor: string;
  if (!color) {
    resolvedColor = undefined;
  } else if (themeTokenColors[color]) {
    resolvedColor = themeTokenColors[color];
  } else {
    resolvedColor = resolveThemeColor(color, muiTheme);
  }

  const combinedSx = {
    width: sizeInPx,
    height: sizeInPx,
    fontSize: sizeInPx,
    color: resolvedColor,
    display: 'inline-block',
    verticalAlign: 'sub',
    flexShrink: 0,
    ...pounceStyles,
    ...(sx || {}),
  };

  return (
    <SvgIcon
      ref={ref}
      viewBox={viewBox}
      titleAccess={title}
      role="graphics-document"
      aria-hidden={!title ? true : undefined}
      sx={combinedSx}
      {...otherProps}
    >
      {description && <desc>{description}</desc>}
      {icon?.path}
    </SvgIcon>
  );
});
